import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  Link as ConnectIcon,
  LinkOff as DisconnectIcon,
  Google as GoogleIcon,
  Edit as WriteIcon,
} from "@mui/icons-material";
import {
  useDisconnectWorkspace,
  useGoogleScopeCatalogue,
  useStartWorkspaceConnect,
  useWorkspaceStatus,
} from "@/hooks/useWorkspace";

const SCOPE_LABELS: Record<string, string> = {
  openid: "Identity",
  email: "Email address",
  profile: "Profile",
  "https://www.googleapis.com/auth/gmail.readonly": "Read Gmail",
  "https://www.googleapis.com/auth/gmail.compose": "Draft + send email",
  "https://www.googleapis.com/auth/gmail.send": "Send email",
  "https://www.googleapis.com/auth/calendar.readonly": "Read Calendar",
  "https://www.googleapis.com/auth/calendar.events": "Manage Calendar events",
  "https://www.googleapis.com/auth/drive.readonly": "Read Drive files",
  "https://www.googleapis.com/auth/drive.metadata.readonly": "List Drive files",
  "https://www.googleapis.com/auth/drive.file": "Create Drive files",
  "https://www.googleapis.com/auth/documents.readonly": "Read Docs",
  "https://www.googleapis.com/auth/documents": "Edit Docs",
  "https://www.googleapis.com/auth/spreadsheets.readonly": "Read Sheets",
  "https://www.googleapis.com/auth/spreadsheets": "Edit Sheets",
  "https://www.googleapis.com/auth/presentations.readonly": "Read Slides",
  "https://www.googleapis.com/auth/presentations": "Edit Slides",
  "https://www.googleapis.com/auth/contacts.readonly": "Read Contacts",
};

function scopeLabel(scope: string): string {
  return SCOPE_LABELS[scope] ?? scope;
}

export default function WorkspaceConnection() {
  const [params, setParams] = useSearchParams();
  const callbackStatus = params.get("status");

  const { data: status, isLoading, refetch } = useWorkspaceStatus();
  const { data: catalogue } = useGoogleScopeCatalogue();
  const startMutation = useStartWorkspaceConnect();
  const disconnectMutation = useDisconnectWorkspace();
  const [feedback, setFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(
    null,
  );

  const writeScopesGranted =
    !!status?.connected &&
    !!catalogue?.write?.length &&
    catalogue.write.every((s) => status.granted_scopes.includes(s));

  useEffect(() => {
    if (callbackStatus === "connected") {
      setFeedback({ kind: "success", message: "Google Workspace connected." });
      void refetch();
      params.delete("status");
      setParams(params, { replace: true });
    } else if (callbackStatus === "error") {
      setFeedback({
        kind: "error",
        message:
          "Google Workspace connection failed. Most common cause: Google didn't return a refresh token because consent was previously granted — go to your Google Account → Security → Third-party access and remove the existing grant, then try again.",
      });
      params.delete("status");
      setParams(params, { replace: true });
    }
  }, [callbackStatus, params, setParams, refetch]);

  const onConnect = async () => {
    setFeedback(null);
    try {
      const { authorize_url } = await startMutation.mutateAsync({});
      window.location.assign(authorize_url);
    } catch {
      setFeedback({
        kind: "error",
        message:
          "Failed to start OAuth flow. Check that the backend has GOOGLE_OAUTH_CLIENT_ID configured.",
      });
    }
  };

  const onGrantWriteAccess = async () => {
    setFeedback(null);
    if (!catalogue?.read?.length || !catalogue?.write?.length) return;
    // Request the union — Google's include_granted_scopes flag means the
    // user gets a single re-consent screen for the write scopes only.
    const scopes = Array.from(new Set([...catalogue.read, ...catalogue.write]));
    try {
      const { authorize_url } = await startMutation.mutateAsync({ scopes });
      window.location.assign(authorize_url);
    } catch {
      setFeedback({ kind: "error", message: "Failed to start incremental OAuth flow." });
    }
  };

  const onDisconnect = async () => {
    setFeedback(null);
    try {
      await disconnectMutation.mutateAsync();
      setFeedback({ kind: "success", message: "Google Workspace disconnected." });
    } catch {
      setFeedback({ kind: "error", message: "Failed to disconnect." });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Google Workspace connection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your personal Google Workspace so your personal agent can read your inbox, calendar,
        and Drive on demand. Write actions (sending email, creating events, creating Docs) always
        ask for explicit confirmation in chat — the connection alone does not authorise unsupervised
        sends.
      </Typography>

      {feedback && (
        <Alert severity={feedback.kind} sx={{ mb: 2 }}>
          {feedback.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <GoogleIcon fontSize="large" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">Google Workspace</Typography>
                {status?.connected ? (
                  <Typography variant="body2" color="success.main">
                    Connected
                    {status.connected_at &&
                      ` · since ${new Date(status.connected_at).toLocaleString("en-GB")}`}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Not connected
                  </Typography>
                )}
              </Box>
              {status?.connected ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DisconnectIcon />}
                  onClick={onDisconnect}
                  disabled={disconnectMutation.isPending}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<ConnectIcon />}
                  onClick={onConnect}
                  disabled={startMutation.isPending}
                >
                  {startMutation.isPending ? "Starting…" : "Connect Google Workspace"}
                </Button>
              )}
            </Box>

            {status?.connected && status.granted_scopes.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Granted access
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {status.granted_scopes.map((s) => (
                    <Chip key={s} label={scopeLabel(s)} size="small" variant="outlined" />
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Additional scopes (e.g. send email) are requested only when first needed by a tool
                  — Google will show a fresh consent screen for the new scope.
                </Typography>
              </Box>
            )}

            {status?.connected && !writeScopesGranted && catalogue?.write?.length && (
              <Box
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  pt: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="subtitle2">Write access</Typography>
                  <Typography variant="caption" color="text.secondary">
                    The personal agent currently has read-only access. Grant write access to enable
                    drafting + sending email, creating calendar events, and creating
                    Docs/Sheets/Slides. Each individual write action still requires explicit Approve
                    in chat.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<WriteIcon />}
                  onClick={onGrantWriteAccess}
                  disabled={startMutation.isPending}
                >
                  {startMutation.isPending ? "Starting…" : "Grant write access"}
                </Button>
              </Box>
            )}

            {status?.connected && writeScopesGranted && (
              <Typography
                variant="caption"
                color="success.main"
                sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}
              >
                ✓ Write access granted — all personal-agent tools available (writes still gated by
                in-chat confirmation).
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
